/* =====================================================================
   sn-pages.js — scripts des pages SN (JavaScript natif, sans dépendance)
   - <sn-collection> : tri et filtres du catalogue sans recharger la page
     (rendu de section Shopify), encadré des filtres repliable sur mobile,
     pagination, retour arrière du navigateur.
   - <sn-track> : onglets du suivi de commande et suivi par numéro de colis.
   Sans JavaScript, les formulaires fonctionnent normalement.
   ===================================================================== */
(function () {
  'use strict';

  var desktop = window.matchMedia('(min-width: 990px)');

  /* -------------------------------------------------------------------
     Catalogue
     ------------------------------------------------------------------- */
  if (!customElements.get('sn-collection')) {
    class SnCollection extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.form = this.querySelector('form');
        this.sectionId = this.dataset.sectionId;
        if (!this.form || !this.sectionId) return;

        this.timer = null;
        this.controller = null;
        this.initialQuery = window.location.search.slice(1);

        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onPopState = this.onPopState.bind(this);
        this.syncToggle = this.syncToggle.bind(this);

        this.form.addEventListener('change', this.onChange);
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submit(0);
        });
        this.addEventListener('click', this.onClick);
        window.addEventListener('popstate', this.onPopState);
        if (desktop.addEventListener) desktop.addEventListener('change', this.syncToggle);

        this.classList.add('is-ready');
        this.syncToggle();
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
        if (desktop.removeEventListener) desktop.removeEventListener('change', this.syncToggle);
      }

      /* Paramètres du formulaire (tri + filtres), sans les champs vides */
      query() {
        var params = new URLSearchParams();
        new FormData(this.form).forEach(function (value, key) {
          if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
        });
        return params.toString();
      }

      onChange(event) {
        var isPrice = event.target && event.target.hasAttribute('data-sn-price');
        this.submit(isPrice ? 700 : 250);
      }

      submit(delay) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.load(this.query(), true), delay);
      }

      onClick(event) {
        var toggle = event.target.closest('[data-sn-filters-toggle]');
        if (toggle && this.contains(toggle)) {
          if (desktop.matches) return;
          var box = toggle.closest('.sn-filters');
          if (!box) return;
          box.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', box.classList.contains('is-open') ? 'true' : 'false');
          return;
        }

        var link = event.target.closest('a[data-sn-ajax]');
        if (link && this.contains(link)) {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
          event.preventDefault();
          var url = new URL(link.href, window.location.href);
          url.searchParams.delete('section_id');
          var scroll = link.hasAttribute('data-sn-scroll');
          this.load(url.searchParams.toString(), true).then((ok) => {
            if (ok && scroll) this.scrollToTop();
          });
        }
      }

      onPopState(event) {
        var state = event.state;
        var query = state && typeof state.snCollection === 'string' ? state.snCollection : this.initialQuery;
        this.load(query, false);
      }

      scrollToTop() {
        var header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
        var top = this.getBoundingClientRect().top + window.scrollY - header - 16;
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(top, 0), behavior: reduce ? 'auto' : 'smooth' });
      }

      /* Remplace les zones [data-sn-replace] par celles renvoyées par Shopify */
      load(query, push) {
        var focusedId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : '';
        var url = window.location.pathname + '?section_id=' + encodeURIComponent(this.sectionId) + (query ? '&' + query : '');
        var pageUrl = window.location.pathname + (query ? '?' + query : '');

        if (this.controller) this.controller.abort();
        this.controller = 'AbortController' in window ? new AbortController() : null;
        this.setAttribute('aria-busy', 'true');

        return fetch(url, { credentials: 'same-origin', signal: this.controller ? this.controller.signal : undefined })
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          })
          .then((html) => {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            this.querySelectorAll('[data-sn-replace]').forEach(function (zone) {
              var fresh = zone.id ? doc.getElementById(zone.id) : null;
              if (fresh) zone.innerHTML = fresh.innerHTML;
            });
            this.cleanLinks();
            this.syncSort(query);
            this.syncToggle();
            if (push) window.history.pushState({ snCollection: query }, '', pageUrl);
            if (focusedId) {
              var target = document.getElementById(focusedId);
              if (target) target.focus({ preventScroll: true });
            }
            this.removeAttribute('aria-busy');
            return true;
          })
          .catch((error) => {
            if (error && error.name === 'AbortError') return false;
            /* En cas d'échec, on recharge simplement la page avec les mêmes paramètres */
            window.location.assign(pageUrl);
            return false;
          });
      }

      /* Les liens rendus via section_id peuvent contenir ce paramètre : on le retire */
      cleanLinks() {
        this.querySelectorAll('a[href*="section_id="]').forEach(function (link) {
          var url = new URL(link.getAttribute('href'), window.location.href);
          url.searchParams.delete('section_id');
          link.setAttribute('href', url.pathname + url.search + url.hash);
        });
      }

      syncSort(query) {
        var select = this.querySelector('[data-sn-sort]');
        if (!select) return;
        var value = new URLSearchParams(query).get('sort_by');
        if (value && select.value !== value) select.value = value;
      }

      syncToggle() {
        this.querySelectorAll('.sn-filters').forEach(function (box) {
          var toggle = box.querySelector('[data-sn-filters-toggle]');
          if (!toggle) return;
          var open = desktop.matches || box.classList.contains('is-open');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }
    }

    customElements.define('sn-collection', SnCollection);
  }

  /* -------------------------------------------------------------------
     Suivi de commande
     ------------------------------------------------------------------- */
  if (!customElements.get('sn-track')) {
    class SnTrack extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
        var tablist = this.querySelector('[role="tablist"]');
        var panels = this.tabs
          .map(function (tab) {
            return document.getElementById(tab.getAttribute('aria-controls'));
          })
          .filter(Boolean);

        if (tablist && this.tabs.length > 1 && panels.length === this.tabs.length) {
          tablist.hidden = false;
          this.tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => this.select(index, false));
            tab.addEventListener('keydown', (event) => this.onKey(event, index));
          });
          this.select(0, false);
        }

        /* Suivi par numéro : l'adresse de base reçoit le numéro saisi */
        this.querySelectorAll('form[data-sn-track-base]').forEach(function (form) {
          form.addEventListener('submit', function (event) {
            var input = form.querySelector('input');
            var value = input ? input.value.replace(/\s+/g, '') : '';
            if (!value) return;
            event.preventDefault();
            window.location.assign(form.getAttribute('data-sn-track-base') + encodeURIComponent(value));
          });
        });
      }

      select(index, focus) {
        this.tabs.forEach(function (tab, i) {
          var selected = i === index;
          var panel = document.getElementById(tab.getAttribute('aria-controls'));
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
          if (panel) panel.hidden = !selected;
        });
        if (focus) this.tabs[index].focus();
      }

      onKey(event, index) {
        var last = this.tabs.length - 1;
        var next = null;
        if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
        if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = last;
        if (next === null) return;
        event.preventDefault();
        this.select(next, true);
      }
    }

    customElements.define('sn-track', SnTrack);
  }
})();
